/**
 * OtpPage — step 2 of authentication.
 *
 * Reaches this page from LoginPage with router state:
 *   { phone: "+989...", resendAvailableIn: 60, expiresIn: 120 }
 *
 * If state is missing (e.g., user reloaded, or navigated here directly),
 * we redirect back to /login.
 *
 * Behavior:
 *   - 5-digit OTP input, numeric only, accepts both Latin and Persian digits
 *   - Countdown 1: code expiry (120s default) — when it hits 0 we show "expired"
 *     and disable the verify button. User must resend.
 *   - Countdown 2: resend cooldown (60s default) — when it hits 0 the resend
 *     button becomes enabled.
 *   - Web OTP API auto-fill: on supported mobile browsers, we listen for an
 *     incoming SMS containing the code (requires backend to format the SMS
 *     with the @domain #code suffix — see BACKEND_CONTRACT.md).
 *   - On 400 INVALID_OTP with attemptsRemaining: show count.
 *   - On 423 LOCKED: show locked message, disable inputs.
 *   - On success: dispatch loginSuccess + navigate to /signup (new user)
 *     or /welcome (existing user — the post-login chooser).
 *
 * Backend contract: POST /auth/verify-otp
 *   Body:     { phone: "+989...", code: "12345" }
 *   Success:  { data: { accessToken, isNewUser, user } } + refresh_token cookie
 *   Errors:   400 INVALID_OTP (details.attemptsRemaining), 423 LOCKED
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Container,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Link,
} from '@mui/material';
import SmsIcon from '@mui/icons-material/Sms';

import LanguageToggle from '@/components/LanguageToggle';
import { authApi, isMockMode } from '@/services/api';
import { loginSuccess } from '@/store/authSlice';
import { formatPhoneDisplay } from '@/utils/phone';
import { normalizeDigits } from '@/utils/digits';

const OTP_LENGTH = 5;

const buildSchema = (t) =>
  z.object({
    code: z
      .string()
      .transform(normalizeDigits)
      .pipe(
        z
          .string()
          .length(OTP_LENGTH, t('auth.otpInvalid'))
          .regex(/^\d+$/, t('auth.otpInvalid'))
      ),
  });

export function OtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Pull state from the navigation. If missing, render <Navigate /> below.
  const phone = location.state?.phone;
  const initialResendIn = location.state?.resendAvailableIn ?? 60;
  const initialExpiresIn = location.state?.expiresIn ?? 120;

  const [secondsToResend, setSecondsToResend] = useState(initialResendIn);
  const [secondsToExpiry, setSecondsToExpiry] = useState(initialExpiresIn);
  const [serverError, setServerError] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(buildSchema(t)),
    defaultValues: { code: '' },
    mode: 'onSubmit',
  });

  /* -------------------------------------------------------------------------
   * Countdowns
   * ----------------------------------------------------------------------- */
  // One interval drives both counters. Stops when both reach 0.
  useEffect(() => {
    const id = setInterval(() => {
      setSecondsToResend((s) => (s > 0 ? s - 1 : 0));
      setSecondsToExpiry((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  /* -------------------------------------------------------------------------
   * Web OTP API (auto-fill from SMS on supported browsers — mainly Chrome
   * Android). Requires HTTPS in production and SMS body to end with:
   *     @your-domain.com #12345
   * If the browser doesn't support it, we silently skip — manual entry works.
   * ----------------------------------------------------------------------- */
  const abortRef = useRef(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('OTPCredential' in window)) return;
    if (isLocked) return;

    const ac = new AbortController();
    abortRef.current = ac;

    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then((otp) => {
        if (otp?.code) {
          setValue('code', otp.code, { shouldValidate: true });
          // Auto-submit after fill — matches user expectation on mobile.
          handleSubmit(onVerify)();
        }
      })
      .catch(() => {
        // User cancelled, no SMS received, or feature not supported on this
        // browser. Nothing to do — manual entry still works.
      });

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked]);

  /* -------------------------------------------------------------------------
   * Submit handler — verify the OTP and route accordingly.
   * ----------------------------------------------------------------------- */
  const onVerify = async ({ code }) => {
    setServerError(null);
    const normalized = normalizeDigits(code);

    try {
      const result = await authApi.verifyOtp(phone, normalized);
      // Persist auth state in Redux. Refresh-token cookie is set by backend.
      dispatch(
        loginSuccess({
          accessToken: result.accessToken,
          user: result.user,
          // verify-otp is authoritative: it tells us whether this phone has a
          // profile, so the answer is known from here on and nothing
          // downstream has to infer it from a null user.
          profileLoaded: true,
        })
      );

      // Route based on whether the user has completed signup.
      if (result.isNewUser) {
        navigate('/signup', { replace: true });
      } else {
        // A pending face-capture link (from an SMS the clinic sent) wins, so
        // the deep-link survives the login detour.
        const pendingInvite = (() => {
          try { return sessionStorage.getItem('pendingCaptureInvite'); } catch { return null; }
        })();
        const pendingRerun = (() => {
          try { return sessionStorage.getItem('pendingRerunId'); } catch { return null; }
        })();
        if (pendingInvite) {
          // Don't clear it here — PublicRoute may also redirect on the auth flip;
          // both must see it and agree on /face-capture. FaceCapturePage clears
          // it once it has arrived and resolved the invite.
          navigate(`/face-capture?invite=${encodeURIComponent(pendingInvite)}`, { replace: true });
        } else if (pendingRerun) {
          // A re-run payment link support texted them. Same contract as the
          // capture link: left in place here so PublicRoute agrees on the
          // destination; RerunPayPage clears it once it has arrived.
          navigate(`/rerun/${encodeURIComponent(pendingRerun)}`, { replace: true });
        } else {
          // Existing users land on the post-login chooser (enter a clinic
          // vs. personal panel), not straight on the dashboard.
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (err) {
      handleVerifyError(err);
    }
  };

  /**
   * Map verify-otp errors to UI state.
   * @param {any} err
   */
  const handleVerifyError = (err) => {
    const status = err?.response?.status;
    const code = err?.response?.data?.error?.code;
    const details = err?.response?.data?.error?.details;

    if (status === 423 || code === 'LOCKED') {
      setIsLocked(true);
      setServerError(t('auth.lockedOut'));
      return;
    }
    if (status === 400 && code === 'INVALID_OTP') {
      const remaining = details?.attemptsRemaining;
      if (typeof remaining === 'number') {
        setServerError(
          t('auth.otpInvalidWithAttempts', { count: remaining })
        );
      } else {
        setServerError(t('auth.otpInvalid'));
      }
      // Clear the field so the user can retry without manual delete.
      reset({ code: '' });
      return;
    }
    if (!err?.response) {
      setServerError(t('auth.networkError'));
      return;
    }
    setServerError(t('errors.serverError'));
  };

  /* -------------------------------------------------------------------------
   * Resend handler — requests a fresh OTP and restarts both countdowns.
   * ----------------------------------------------------------------------- */
  const handleResend = async () => {
    if (secondsToResend > 0 || isResending || isLocked) return;
    setIsResending(true);
    setServerError(null);
    try {
      const { resendAvailableIn, expiresIn } = await authApi.requestOtp(phone);
      setSecondsToResend(resendAvailableIn ?? 60);
      setSecondsToExpiry(expiresIn ?? 120);
      reset({ code: '' });
    } catch (err) {
      const status = err?.response?.status;
      const details = err?.response?.data?.error?.details;
      if (status === 429) {
        setSecondsToResend(details?.retryAfter ?? 60);
        setServerError(
          t('auth.rateLimited', { seconds: details?.retryAfter ?? 60 })
        );
      } else if (!err?.response) {
        setServerError(t('auth.networkError'));
      } else {
        setServerError(t('errors.serverError'));
      }
    } finally {
      setIsResending(false);
    }
  };

  /* -------------------------------------------------------------------------
   * Guard: if no phone in state, the user landed here directly. Bounce them
   * back to /login. This must happen AFTER all hooks (Rules of Hooks).
   * ----------------------------------------------------------------------- */
  if (!phone) {
    return <Navigate to="/login" replace />;
  }

  const isExpired = secondsToExpiry === 0;
  const canVerify = !isSubmitting && !isLocked && !isExpired;

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2,
      background: 'linear-gradient(160deg,#11223E 0%,#0A1425 100%)' }}>
      <Box sx={{ position: 'absolute', top: 12, insetInlineEnd: 12, color: '#fff' }}>
        <LanguageToggle />
      </Box>
      <Container maxWidth="xs" disableGutters sx={{ width: '100%' }}>
        <Paper elevation={8} sx={{ p: 4, borderRadius: 4 }}>
          <Box textAlign="center" sx={{ mb: 3 }}>
            <SmsIcon sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" component="h1" fontWeight={800}>
              {t('auth.otpTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('auth.otpSubtitle', { phone: formatPhoneDisplay(phone) })}
            </Typography>
          </Box>
          <form onSubmit={handleSubmit(onVerify)} noValidate>
            <Stack spacing={2}>
              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('auth.otpLabel')}
                    helperText={
                      errors.code?.message ??
                      (isExpired
                        ? t('auth.otpExpired')
                        : t('auth.otpExpiresIn', { seconds: secondsToExpiry }))
                    }
                    fullWidth
                    autoFocus
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    error={!!errors.code || isExpired}
                    disabled={!canVerify}
                    onChange={(e) => {
                      // Normalize as the user types so they see Latin digits
                      // and we never send Persian digits to the backend.
                      const v = normalizeDigits(e.target.value)
                        .replace(/\D/g, '')
                        .slice(0, OTP_LENGTH);
                      field.onChange(v);
                    }}
                    inputProps={{
                      dir: 'ltr',
                      maxLength: OTP_LENGTH,
                      style: {
                        textAlign: 'center',
                        letterSpacing: '0.5em',
                        fontSize: '1.5rem',
                      },
                    }}
                  />
                )}
              />

              {serverError && (
                <Alert severity={isLocked ? 'warning' : 'error'}>
                  {serverError}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!canVerify}
                fullWidth
              >
                {isSubmitting ? t('auth.verifying') : t('auth.verifyOtp')}
              </Button>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate('/login', { replace: true })}
                  underline="hover"
                >
                  {t('auth.changePhone')}
                </Link>

                {secondsToResend > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('auth.resendIn', { seconds: secondsToResend })}
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleResend}
                    disabled={isResending || isLocked}
                    underline="hover"
                  >
                    {t('auth.resendOtp')}
                  </Link>
                )}
              </Stack>

              {isMockMode && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                >
                  {t('auth.mockHint')}
                </Typography>
              )}
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}

export default OtpPage;
